import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogActions, DialogTitle, IconButton } from "@mui/material";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import Cookies from "universal-cookie";
import RenderContent from "./RenderContent";
import RenderFooter from "./RenderFooter";
import accountAPI from "../../../Network/Account";

const cookies = new Cookies();

export default function FormContainer({ object, openForm, setOpenForm, loadData }) {
    const [t] = useTranslation("common");
    const maintheme = useSelector((state) => state.themeData.maintheme);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        account_name: "",
        username: "",
        password: "",
        is_governmental: 0,
        contractor_id: null,
        municipality_id: null,
        municipality_name: "",
        enabled: 1,
    });

    useEffect(() => {
        if (object) {
            setFormData({
                account_name: object?.account_name || "",
                username: object?.username || "",
                password: "", // Don't show password on edit
                is_governmental: object?.is_governmental ?? 0,
                contractor_id: object?.contractor_id || null,
                municipality_id: object?.municipality_id || null,
                municipality_name: object?.municipality_name || "",
                enabled: object?.enabled ?? 1,
            });
        }
    }, [object]);

    const handleClose = () => {
        setOpenForm(false);
    };

    const validate = () => {
        if (!formData.account_name || formData.account_name.length < 4 || formData.account_name.length > 50) {
            toast.error(t("ACCOUNTS.name_validation") || "Account Name must be between 4 and 50 characters");
            return false;
        }
        if (!object && (!formData.username || formData.username.length < 4 || formData.username.length > 50)) {
            toast.error(t("ACCOUNTS.username_validation") || "Username must be between 4 and 50 characters");
            return false;
        }
        if (!object) {
            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?]).{8,100}$/;
            if (!formData.password || !passwordRegex.test(formData.password)) {
                toast.error(t("ACCOUNTS.password_validation") || "Password must be 8-100 characters and contain upscale, lowercase, number, and special character");
                return false;
            }
        }
        if (!formData.municipality_id) {
            toast.error(t("ACCOUNTS.municipality_required") || "Municipality is mandatory");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            let result;
            const authParams = {
                username: cookies.get("user_name") ? cookies.get("user_name") : null,
                user_id: cookies.get("user_id") ? cookies.get("user_id") : null,
                account_name: cookies.get("account_name") ? cookies.get("account_name") : null,
                account_id: cookies.get("account_id") ? cookies.get("account_id") : null,
                authHash: cookies.get("authHash") ? cookies.get("authHash") : null,
                sid: cookies.get("sid") ? cookies.get("sid") : null,
                baseurl: cookies.get("baseUrl") ? cookies.get("baseUrl") : null,
                id: cookies.get("id") ? cookies.get("id") : null,
            };

            if (object?.account_id) {
                result = await accountAPI.editAccount({
                    id: object.account_id,
                    data: formData,
                    params: authParams
                });
            } else {
                result = await accountAPI.addAccount({
                    data: formData,
                    params: authParams
                });
            }

            if (result?.status && result?.data?.status) {
                toast.success(
                    object?.account_id
                        ? t("NETWORK_MESSAGES.updateSuccess") || "تم التحديث بنجاح"
                        : t("NETWORK_MESSAGES.addSuccess") || "تمت الإضافة بنجاح"
                );
                handleClose();
                loadData();
            } else {
                toast.error(
                    result?.data?.data?.message || t("NETWORK_MESSAGES.unknownError")
                );
            }
        } catch (error) {
            toast.error(t("NETWORK_MESSAGES.unknownError"));
        }
        setLoading(false);
    };

    return (
        <Dialog
            open={openForm}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "12px" } }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: maintheme?.primary,
                    color: "#fff",
                    fontFamily: "Cairo-Bold",
                }}
            >
                {object?.account_id
                    ? t("ACCOUNTS.edit_title") || "تعديل حساب"
                    : t("ACCOUNTS.add_title") || "إضافة حساب"}
                <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "24px" }}>
                <RenderContent formData={formData} setFormData={setFormData} isEdit={!!object?.account_id} />
            </DialogContent>
            <DialogActions sx={{ padding: "16px 24px" }}>
                <RenderFooter
                    handleClose={handleClose}
                    handleSubmit={handleSubmit}
                    loading={loading}
                    isEdit={!!object?.account_id}
                />
            </DialogActions>
        </Dialog>
    );
}
